import { useState, CSSProperties, MouseEvent } from "react";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { HideIcon, ShowIcon, SearchIcon } from "../../assets/images/svg";
import { SxProps, Theme } from "@mui/system";
import SimpleText from "../text/SimpleText";

interface PropType {
  inputLabel?: string;
  validateStatus?: "" | "success" | "warning" | "error" | "validating";
  help?: string;
  placeHolderLabel?: string;
  value: string | number;
  type?: "string" | "number";
  style?: CSSProperties;
  disabled?: boolean;
  tooltipText?: string;
  placement?: "top" | "left" | "right" | "bottom";
  setParentState: (value: string) => void;
  password?: boolean;
  search?: boolean;
  multipleLine?: number;
  sx?: SxProps<Theme>;
}

const SimpleInput = ({
  inputLabel = "",
  placeHolderLabel = "",
  value,
  setParentState,
  validateStatus = "",
  help = "",
  type,
  style = {},
  disabled = false,
  tooltipText = "",
  password = false, // Default to false
  placement = "bottom",
  search = false,
  multipleLine,
  sx,
}: PropType) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Tooltip
      title={tooltipText}
      placement={placement}
      arrow
      disableHoverListener={!tooltipText}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box style={style}>
        <SimpleText value={inputLabel} type="labelText" />
        <Box display="flex" alignItems="center">
          <TextField
            variant="outlined"
            fullWidth
            type={password && !showPassword ? "password" : type}
            value={value}
            onChange={(e) => setParentState(e.target.value)}
            placeholder={t(placeHolderLabel) ?? ""}
            disabled={disabled}
            helperText={t(help)}
            error={validateStatus === "error" || Boolean(help)}
            multiline={Boolean(multipleLine)}
            rows={multipleLine || 1}
            sx={{
              "& .MuiInputBase-input": {
                height: 7,
                paddingLeft: multipleLine ? 0 : 1.5,
              },
              width: "100%",
              background: "white",
              borderRadius: 1,
              // Hide spinner (stepper) for number input type
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none", // camelCase for webkit properties
                  margin: 0,
                },
              "& input[type=number]": {
                MozAppearance: "textfield", // camelCase for Firefox
              },
            }}
            InputProps={{
              startAdornment: search && (
                <InputAdornment position="start">
                  <SearchIcon color="gray" />
                </InputAdornment>
              ),
              endAdornment: password && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <HideIcon /> : <ShowIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            FormHelperTextProps={{
              sx: {
                color: "red",
                fontSize: "12px",
                marginLeft: "1px",
                marginTop: "-1px",
              },
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default SimpleInput;
